import { field } from '../fields'
import Modal from './Modal'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import {
  createGroup,
  deleteGroup,
  updateGroup,
  type AttendanceGroup,
} from '../api/attendanceGroups'
import type { ClassRow } from '../api/classAdmin'
/**
 * Classes a dancer chooses between (D-170), made from a selection (D-205).
 *
 * <b>The same gesture as filing classes under a kind.</b> Tick the classes,
 * apply the grouping — one mechanic rather than two workflows, and this
 * replaces a whole screen whose only job was a class picker with a level filter
 * bolted on. The list this opens from already has search, day and programme
 * filters, so the picker had nothing left to offer.
 *
 * <b>Nothing is inferred from a class name.</b> The real export makes parsing
 * impossible — combos span levels, pair across days, and one carries a stray
 * space — and a wrong guess here tells a dancer she cannot compete (D-88).
 */
export default function CountsAsOneDialog({
  selection,
  existing,
  onClose,
}: {
  selection: ClassRow[]
  /** The set every selected class already belongs to, when they share one. */
  existing: AttendanceGroup | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()

  const [name, setName] = useState(existing?.name ?? suggestedName(selection))
  const [required, setRequired] = useState(existing?.requiredPerWeek ?? 1)

  const done = (groups: AttendanceGroup[]) => {
    queryClient.setQueryData(['attendance-groups'], groups)
    queryClient.invalidateQueries({ queryKey: ['admin-classes'] })

    // Every standing in the studio just changed meaning.
    queryClient.invalidateQueries({ queryKey: ['dancer-standing'] })
    queryClient.invalidateQueries({ queryKey: ['class-standings'] })

    onClose()
  }

  const save = useMutation({
    mutationFn: () => {
      const input = {
        // A pair has no other answer, and the box that used to say so is gone.
        name,
        requiredPerWeek: selection.length === 2 ? 1 : required,
        studioClassIds: selection.map((c) => c.classId),
      }

      return existing ? updateGroup(existing.id, input) : createGroup(input)
    },
    onSuccess: done,
  })

  const remove = useMutation({
    mutationFn: () => deleteGroup(existing!.id),
    onSuccess: done,
  })

  const tooMany = required > selection.length

  /*
   * Two classes is the whole job, and for two there is nothing to configure:
   * "attend 1 of 2" is the only answer a pair can have. A number box that can
   * only hold the number already in it is a decision offered where none exists,
   * and it was the part that made this read as complicated (D-226).
   *
   * Three or more is a real choice — 1 of 3 and 2 of 3 both happen — so the box
   * comes back exactly when it starts meaning something.
   */
  const pair = selection.length === 2

  return (
    /*
      <b>On the shared shell</b> (D-515). It had no `role`, no `aria-modal` and
      no name, so a screen reader announced it as an anonymous group; nothing
      closed it on Escape; and the backdrop was an absolutely-positioned
      `<button aria-label="Cancel">` — which does dismiss on a tap, and also
      puts a control in the tab order that reads as a button and is really the
      dim area behind the dialog.
    */
    <Modal
      onDismiss={onClose}
      label={existing ? 'Edit what is required' : 'Attend one of these'}
      overlayClassName="fixed inset-0 z-50 grid place-items-center bg-black/40 px-5 backdrop-blur-[1px]"
      panelClassName="relative w-full max-w-[26rem] rounded-2xl border border-line bg-surface px-5 py-4 shadow-2xl"
    >
        <h2 className="font-display text-lg">
          {existing ? 'Edit what is required' : 'Attend one of these'}
        </h2>
        <p className="mt-1 text-[13px] text-ink-2">
          {pair
            ? 'A dancer comes to one or the other. Missing the other one will not count against their attendance.'
            : 'A dancer picks between them. Missing the others will not count against their attendance.'}
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-semibold text-ink-3">
              What do you call this, for your own reference
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="Level 2 tech"
              className={field}
            />
          </label>

          {pair ? (
            <p className="text-[13px] text-ink-3">
              Must attend <b className="tabular text-ink">1</b> of the two each week.
            </p>
          ) : (
            <label className="flex items-center gap-2 text-[13px]">
              <span className="font-semibold text-ink-3">Must attend</span>
              <input
                type="number"
                min={1}
                max={Math.max(1, selection.length)}
                value={required}
                onChange={(e) => setRequired(Math.max(1, Number(e.target.value) || 1))}
                className="tabular w-16 rounded-lg border border-line-2 bg-surface px-2 py-1.5 text-center outline-none focus:border-accent"
              />
              <span className="text-ink-3">of {selection.length} each week</span>
            </label>
          )}

          {/*
            Said plainly rather than left to be discovered. A rule requiring as
            many classes as it covers is every class required, which is the same
            as no rule — harmless, but nobody should believe a rule is in force
            when it is not. Unreachable for a pair, which is fixed at 1 of 2.
          */}
          {required >= selection.length && !tooMany && (
            <p className="rounded-lg bg-warn-bg px-3 py-2 text-[12px] text-ink-2">
              That asks for every one of these, so nothing is optional. Add another class, or
              lower how many are required.
            </p>
          )}

          <ul className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-lg border border-line bg-surface-2 px-3 py-2">
            {selection.map((c) => (
              <li key={c.classId} className="text-[12px] wrap-anywhere text-ink-2">
                {c.name}
              </li>
            ))}
          </ul>

          {(save.isError || remove.isError) && (
            <p role="alert" className="text-[12.5px] text-crit">
              {((save.error ?? remove.error) as Error).message}
            </p>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={!name.trim() || tooMany || save.isPending}
              className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
            >
              {save.isPending ? 'Saving...' : 'Save'}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
            >
              Cancel
            </button>

            {existing && (
              <button
                type="button"
                onClick={() => remove.mutate()}
                className="ml-auto flex items-center gap-1.5 text-[12.5px] font-semibold text-crit hover:underline"
              >
                <Trash2 size={13} strokeWidth={2.5} />
                Delete
              </button>
            )}
          </div>

          {/* The consequence of deleting, said before rather than after. */}
          {existing && (
            <p className="text-[11.5px] text-ink-3">
              Deleting this makes every class in it count separately again.
            </p>
          )}
        </div>
    </Modal>
  )
}

/**
 * A starting name from what the selection has in common.
 *
 * <b>A suggestion, never the answer.</b> The names are machine-shaped and the
 * common prefix is often useful — `COMP_TECH_BALLET_2` and `COMP_TECH_COMBO_2`
 * share `COMP_TECH_` — but it is offered in an editable box, because a name
 * derived wrongly here is only a name, and one *inferred* would be a rule.
 */
export function suggestedName(selection: ClassRow[]): string {
  if (selection.length === 0) return ''

  const [first, ...rest] = selection.map((c) => c.name)

  let shared = first
  for (const name of rest) {
    let i = 0
    while (i < shared.length && i < name.length && shared[i] === name[i]) i++
    shared = shared.slice(0, i)
  }

  // Trailing separators read as an unfinished word, and two characters is not
  // a name anybody would recognise next season.
  const cleaned = shared.replace(/[\s_|\-–—]+$/, '').trim()
  return cleaned.length >= 3 ? cleaned : ''
}
